import { sleep } from "./sleep.ts";
import { SECONDS } from "./time.ts";
import { isRejected } from "./types/is.ts";

export type ThrottleOptions = {
  /** If passed, will log each error individually */
  logger?: { error: (data: object, message: string) => void };
  /** Maximum number of tasks to run concurrently */
  maxConcurrency?: number;
  /** Maximum number of tasks to run per second */
  maxPerSecond?: number;
};

/**
 * Similar to Promise.allSettled() but throttles execution to a maximum number of parallel operations.
 */
export const throttledAllSettled = async <T>(
  tasks: (() => Promise<T>)[],
  options?: ThrottleOptions
): Promise<PromiseSettledResult<Awaited<T>>[]> => {
  const maxConcurrency = options?.maxConcurrency ?? Infinity;
  const maxPerSecond = options?.maxPerSecond ?? Infinity;

  // Short-circuit if there is no throttling taking place
  if (tasks.length <= Math.min(maxConcurrency, maxPerSecond)) {
    const promises = tasks.map((task) => task());
    return Promise.allSettled(promises);
  }
  const results: PromiseSettledResult<Awaited<T>>[] = new Array(
    tasks.length
  ).fill(null);
  let index = 0;
  let requestWindowStart = Date.now();
  let requestsThisWindow = 0;
  let windowWait: Promise<void> | undefined;

  // Copy collection so we don't alter it during operation
  const queue = [...tasks];
  const workers = Array(Math.min(maxConcurrency, tasks.length)).fill({});

  const waitForRequestSlot = async (): Promise<void> => {
    if (requestsThisWindow < maxPerSecond) {
      requestsThisWindow++;
      return;
    }
    if (!windowWait) {
      const timeToWait = Math.max(0, requestWindowStart + SECONDS - Date.now());
      windowWait = sleep(timeToWait).then(() => {
        requestWindowStart = Date.now();
        requestsThisWindow = 0;
        windowWait = undefined;
      });
    }
    await windowWait;
    await waitForRequestSlot();
  };

  const loop = async (): Promise<void> => {
    // remember current `index` because it could be mutated by another worker by the time an `await` expression completes inside the `loop` function
    const currentIdx = index;
    const next = queue[currentIdx];
    index++;
    if (next) {
      await waitForRequestSlot();
      try {
        const result = await next();
        results[currentIdx] = { status: "fulfilled", value: result };
      } catch (error: any) {
        options?.logger?.error(
          {
            ...error,
            index: currentIdx,
            error,
            stack: new Error().stack,
          },
          "Error during task execution"
        );
        results[currentIdx] = { status: "rejected", reason: error };
      }
      await loop();
    }
  };
  return Promise.allSettled(workers.map(loop)).then(() => results);
};

/**
 * Similar to Promise.all() but throttles execution to a maximum number of parallel operations
 */
export const throttledAll = async <T>(
  tasks: (() => Promise<T>)[],
  options?: { maxConcurrency?: number }
): Promise<T[]> => {
  const settled = await throttledAllSettled(tasks, options);
  const reject = settled.find(isRejected);
  if (reject) throw reject.reason;
  return settled.map((s) => (s as PromiseFulfilledResult<T>).value);
};
